import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const WarrantyPeriodSettings = () => {
  const [editingWarranty, setEditingWarranty] = useState({});
  
  // 공사 유형 목록 조회
  const { data: constructionTypes } = useQuery(
    'construction-types',
    () => api.get('/settings/construction-types').then(res => res.data),
    {
      onError: (error) => {
        console.error('공사 유형 조회 실패:', error);
        toast.error('공사 유형을 불러오는데 실패했습니다');
      }
    }
  );
  
  // 보증 기간 조회
  const { data: warrantyPeriods, refetch: refetchWarrantyPeriods } = useQuery(
    'warranty-periods',
    () => api.get('/warranties/periods').then(res => res.data),
    {
      onError: (error) => {
        console.error('보증 기간 조회 실패:', error);
        toast.error('보증 기간을 불러오는데 실패했습니다');
      },
      onSuccess: (data) => {
        // 조회된 데이터를 editingWarranty 상태에 설정
        const warrantyMap = {};
        data.forEach(item => {
          warrantyMap[item.construction_type] = item.warranty_months;
        });
        setEditingWarranty(warrantyMap);
      }
    }
  );
  
  // 보증 기간 업데이트
  const updateWarrantyMutation = useMutation(
    ({ constructionType, warrantyMonths }) => 
      api.put(`/warranties/periods/${constructionType}`, { warranty_months: warrantyMonths }),
    {
      onSuccess: () => {
        toast.success('보증 기간이 저장되었습니다');
        refetchWarrantyPeriods();
      },
      onError: (error) => {
        toast.error('보증 기간 저장에 실패했습니다');
        console.error(error);
      }
    }
  );
  
  // 일괄 업데이트
  const bulkUpdateMutation = useMutation(
    (updates) => api.put('/warranties/periods/bulk', { updates }),
    {
      onSuccess: () => {
        toast.success('모든 보증 기간이 저장되었습니다');
        refetchWarrantyPeriods();
      },
      onError: (error) => {
        toast.error('보증 기간 저장에 실패했습니다');
        console.error(error);
      }
    }
  );
  
  const handleWarrantyChange = (constructionType, value) => {
    const months = parseInt(value) || 12;
    setEditingWarranty(prev => ({
      ...prev,
      [constructionType]: months
    }));
  };
  
  const handleSave = (constructionType) => {
    const months = editingWarranty[constructionType] || 12;
    updateWarrantyMutation.mutate({ constructionType, warrantyMonths: months });
  };
  
  const handleSaveAll = () => {
    const updates = Object.entries(editingWarranty).map(([constructionType, months]) => ({
      construction_type: constructionType,
      warranty_months: parseInt(months) || 12
    }));
    console.log('Sending updates:', { updates });
    bulkUpdateMutation.mutate(updates);
  };
  
  const setAllWarrantyPeriods = (months) => {
    const newWarrantyMap = {};
    constructionTypes?.forEach(type => {
      newWarrantyMap[type] = months;
    });
    setEditingWarranty(newWarrantyMap);
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-green-600" />
              공사 유형별 보증 기간 설정
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              각 공사 유형별로 기본 보증 기간을 설정합니다. 프로젝트 완료 시 이 기간에 따라 보증서가 발급됩니다.
            </p>
          </div>
          <button
            onClick={handleSaveAll}
            disabled={bulkUpdateMutation.isLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {bulkUpdateMutation.isLoading ? '저장 중...' : '전체 저장'}
          </button>
        </div>
        
        {/* 빠른 설정 버튼 */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">빠른 설정</h4>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setAllWarrantyPeriods(6)}
              className="px-3 py-1.5 bg-gray-600 text-white text-sm font-medium rounded hover:bg-gray-700 transition-colors"
            >
              모두 6개월
            </button>
            <button
              onClick={() => setAllWarrantyPeriods(12)}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
            >
              모두 1년
            </button>
            <button
              onClick={() => setAllWarrantyPeriods(24)}
              className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
            >
              모두 2년
            </button>
            <button
              onClick={() => setAllWarrantyPeriods(36)}
              className="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded hover:bg-purple-700 transition-colors"
            >
              모두 3년
            </button>
          </div>
        </div>
        
        {/* 공사 유형별 보증 기간 설정 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {constructionTypes?.map((type) => {
            const currentValue = editingWarranty[type] || 12;
            const savedValue = warrantyPeriods?.find(w => w.construction_type === type)?.warranty_months || 12;
            const hasChanged = currentValue !== savedValue;
            
            return (
              <div 
                key={type}
                className={`border rounded-lg p-4 transition-colors ${
                  hasChanged ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                }`}
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {type}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={currentValue}
                    onChange={(e) => handleWarrantyChange(type, e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  <span className="text-sm text-gray-600 whitespace-nowrap">개월</span>
                  {hasChanged && (
                    <button
                      onClick={() => handleSave(type)}
                      disabled={updateWarrantyMutation.isLoading}
                      className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      저장
                    </button>
                  )}
                </div>
                {currentValue && (
                  <p className="mt-1 text-xs text-gray-500">
                    {Math.floor(currentValue / 12)}년 {currentValue % 12}개월
                  </p>
                )}
              </div>
            );
          })}
        </div>
        
        {/* 안내 사항 */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <ShieldCheckIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">보증 기간 안내</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>설정된 보증 기간은 프로젝트 완료일을 기준으로 계산됩니다</li>
                  <li>프로젝트별로 개별 보증 기간을 다르게 설정할 수도 있습니다</li>
                  <li>보증서에는 시공 책임자(소장)가 보증 주체로 표시됩니다</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyPeriodSettings;